/**
 * NatureSearchByName — text search across the atlas with automatic
 * spell-check + "Build me this profile" fallback.
 *
 * Placed on `/nature/identify` as the third mode (Photo / Audio /
 * Search by name). Behaviour:
 *
 *   • Pick a category (Birds / Mammals / Flowers / Trees / …) from
 *     the atlas meta endpoint, or leave as "Any category".
 *   • Type a name (common or scientific).
 *   • On Search:
 *       - Substring + difflib fuzzy match via
 *         `GET /nature/atlas/suggest?q=<term>[&category=<cat>]`
 *       - If matches: render each as a card with photo/tagline +
 *         "Add photos & notes →" CTA that deep-links into the profile
 *         page (where our `EntityMessageBoard` lives).
 *       - If none: show the "Build me a '<name>' profile" CTA that
 *         POSTs to `/nature/atlas/request-entry` (spawns AI enrichment
 *         + navigates to the fresh species page).
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Loader2, Sparkles, ArrowRight, ImagePlus, MessageCircle } from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";

export default function NatureSearchByName() {
  const nav = useNavigate();
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);   // null = untouched, [] = no match
  const [busy, setBusy] = useState(false);
  const [requesting, setRequesting] = useState(false);

  // Load categories once.
  useEffect(() => {
    http.get("/nature/atlas/meta")
      .then((r) => {
        const flat = (r.data?.groups || []).flatMap((g) => g.categories || []);
        setCategories(flat);
      })
      .catch(() => setCategories([]));
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    const term = q.trim();
    if (term.length < 2) {
      toast.error("Type at least 2 characters");
      return;
    }
    setBusy(true);
    setItems(null);
    try {
      const url = category
        ? `/nature/atlas/suggest?q=${encodeURIComponent(term)}&category=${encodeURIComponent(category)}&limit=8`
        : `/nature/atlas/suggest?q=${encodeURIComponent(term)}&limit=8`;
      const r = await http.get(url);
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setBusy(false);
    }
  };

  const requestEntry = async () => {
    const term = q.trim();
    if (!term) return;
    if (!category) {
      toast.error("Please pick a category first");
      return;
    }
    setRequesting(true);
    try {
      const r = await http.post("/nature/atlas/request-entry", {
        name: term,
        category,
      });
      const slug = r.data?.slug;
      toast.success(r.data?.created ? "Profile queued — building now" : "This profile already exists");
      if (slug) nav(`/nature/species/${slug}`);
    } catch {
      toast.error("Could not queue this entry. Please try again.");
    } finally {
      setRequesting(false);
    }
  };

  const hasSearched = items !== null;
  const hasHits = (items || []).length > 0;

  return (
    <section
      className="rounded-2xl border p-5 sm:p-6"
      style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-bg)" }}
      data-testid="nature-search-by-name"
    >
      <div className="text-sm opacity-80 mb-3">
        Know what you're looking for? Search our atlas by name — or ask us to build a fresh profile.
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-[180px,1fr,auto] gap-2">
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm bg-transparent"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid="nature-search-category"
        >
          <option value="">Any category</option>
          {categories.map((c) => (
            <option key={c.slug} value={c.slug}>{c.label}</option>
          ))}
        </select>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="e.g. Bald eagle · Plumeria · Boswellia sacra"
            className="w-full pl-9 pr-3 py-2 rounded-lg border bg-transparent text-sm"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid="nature-search-input"
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          className="wofy-btn-primary text-sm"
          data-testid="nature-search-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          {busy ? "Searching…" : "Search"}
        </button>
      </form>

      {/* Results */}
      {hasSearched && (
        <div className="mt-5" data-testid="nature-search-results">
          {hasHits ? (
            <>
              <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>
                Found {items.length} {items.length === 1 ? "match" : "matches"}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {items.map((it) => (
                  <Link
                    key={it.slug}
                    to={`/nature/species/${it.slug}`}
                    className="rounded-xl border p-3 hover:bg-black/5 transition group"
                    style={{ borderColor: "var(--wofy-border)" }}
                    data-testid={`nature-search-hit-${it.slug}`}
                  >
                    <div className="font-medium leading-tight">
                      {it.name || it.common_name || it.slug}
                    </div>
                    {it.scientific_name && (
                      <div className="text-[11px] font-mono italic opacity-70 mt-0.5">
                        {it.scientific_name}
                      </div>
                    )}
                    {it.tagline && <p className="text-xs opacity-80 mt-1 line-clamp-2">{it.tagline}</p>}
                    <div className="mt-2 flex items-center gap-3 text-[11px] font-mono uppercase tracking-wider opacity-70 group-hover:opacity-100">
                      <span className="inline-flex items-center gap-1"><ImagePlus className="w-3 h-3" /> Add photos</span>
                      <span className="inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" /> Discuss</span>
                      <ArrowRight className="w-3 h-3 ml-auto transition-transform group-hover:translate-x-0.5" />
                    </div>
                  </Link>
                ))}
              </div>
              <p className="mt-4 text-xs opacity-70">
                Tip: on each profile you can post photos, observations and start a discussion with the community.
              </p>
            </>
          ) : (
            <div
              className="rounded-2xl border p-5 text-center"
              style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
              data-testid="nature-search-empty"
            >
              <div className="font-display text-lg leading-snug">
                Nothing matches “{q}” yet
              </div>
              <p className="text-sm opacity-70 mt-1 max-w-md mx-auto">
                We can build a full profile — Wikipedia bio, photos, sourced
                links, plus a discussion box — in about a minute.
                {!category && " Pick a category above so we can file it correctly."}
              </p>
              <button
                onClick={requestEntry}
                disabled={requesting || !category}
                className="wofy-btn-primary mt-4 mx-auto"
                data-testid="nature-search-request-btn"
              >
                {requesting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Queuing your request…
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" /> Build me a “{q}” profile <ArrowRight className="w-3 h-3" />
                  </>
                )}
              </button>
              {!category && (
                <p className="text-[11px] opacity-60 mt-2">
                  A category is required before we can queue enrichment.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
